import { Box } from "@chakra-ui/react";
import { Nullable } from "@intentsify/types";
import { isDefined } from "@intentsify/utils";
import { ErrorBoundary } from "components";
import { scaleLinear } from "d3-scale";
import { DateTime } from "luxon";
import { useMemo } from "react";
import { useMeasure } from "react-use";
import { getMinMax } from "../Timeseries/Timeseries.utils";

type SparklinePoint = {
  isoDate: string;
  value: Nullable<number>;
};

type SparklineProps = {
  data: SparklinePoint[];
  margins: {
    top: number;
    right: number;
    bottom: number;
    left: number;
  };
  lineWidth: number;
  color?: string;
  beginAtZero?: boolean;
};

const toTime = (isoDate: string) => DateTime.fromISO(isoDate).toUTC().valueOf();

/**
 * SVG path for the points, breaking the line at missing values. Exported for
 * tests.
 */
export const getSparklinePath = (
  data: SparklinePoint[],
  {
    width,
    height,
    beginAtZero,
  }: { width: number; height: number; beginAtZero: boolean },
) => {
  const points = [...data].sort(
    (a, b) => toTime(a.isoDate) - toTime(b.isoDate),
  );
  const values = points.map((point) => point.value).filter(isDefined);

  if (width <= 0 || height <= 0 || values.length === 0) {
    return "";
  }

  const x = scaleLinear()
    .domain(getMinMax(points.map((point) => toTime(point.isoDate))))
    .range([0, width]);
  const y = scaleLinear()
    .domain(beginAtZero ? [0, Math.max(...values)] : getMinMax(values))
    .range([height, 0])
    .nice();

  let path = "";
  let penDown = false;

  for (const point of points) {
    if (point.value === null) {
      penDown = false;
      continue;
    }

    const px = x(toTime(point.isoDate)) ?? 0;
    const py = y(point.value) ?? height;
    path += `${penDown ? "L" : "M"}${px},${py}`;
    penDown = true;
  }

  return path;
};

/**
 * Tiny trend line. Plain SVG rather than a chart library: tables render many
 * of these at once.
 */
const Sparkline = ({
  data,
  margins,
  lineWidth,
  color,
  beginAtZero = true,
}: SparklineProps) => {
  const [ref, { width, height }] = useMeasure<HTMLDivElement>();
  const plotWidth = width - margins.left - margins.right;
  const plotHeight = height - margins.top - margins.bottom;

  const path = useMemo(
    () =>
      getSparklinePath(data, {
        width: plotWidth,
        height: plotHeight,
        beginAtZero,
      }),
    [data, plotWidth, plotHeight, beginAtZero],
  );

  return (
    <ErrorBoundary>
      <Box ref={ref} width="100%" height="100%">
        {width > 0 && height > 0 && (
          <svg width={width} height={height} style={{ display: "block" }}>
            <path
              d={path}
              transform={`translate(${margins.left},${margins.top})`}
              fill="none"
              stroke={color}
              strokeWidth={lineWidth}
            />
          </svg>
        )}
      </Box>
    </ErrorBoundary>
  );
};

export { Sparkline };
